import React, { useEffect, useMemo, useRef, useState } from 'react';
import PreferredDateField from '../Common/PreferredDateField';
import { API_BASE_URL } from '../../utils/apiBase';
import { clientFacingShootType, StoredQuote } from '../../utils/quoteRecord';
import { toShootTab, type ShootTab } from './shootTab';
import { deriveClientJourney, pickCurrentQuote, STAGE_ORDER, STAGE_TITLES, type JourneyStage } from '../../utils/clientJourney';
import { clientKeyForQuote } from '../../utils/clientKey';
import { listClientJobs, summarizeClientJobs } from '../../utils/clientJobs';
import { formatPreferredDate } from '../../utils/preferredDate';

interface ClientSummary {
  key: string;
  name: string;
  email: string;
  phoneE164: string;
  company?: string;
  billingAddress?: string;
  quoteCount: number;
  latestAt: string;
}

interface StudioEnquiry {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  source: string;
  shootType?: string;
  preferredDate?: string;
  notes?: string;
  receivedAt: string;
  clientKey: string;
  linkedQuoteId?: string | null;
}

interface PaymentSummary {
  lockedMinor: number;
  outstandingMinor: number;
  receivedMinor?: number;
  currentlyDueMinor?: number;
  depositDueMinor?: number;
  depositExpectedMinor?: number;
  depositReceivedMinor?: number;
  payments: Array<{ id: string; type: string; status: string; verifiedMinor: number; note?: string; paidAt?: string | null }>;
}

interface ResolvedJob {
  kind: 'quote' | 'enquiry';
  id: string;
  at: string;
  title: string;
  detail: string;
  journey: ReturnType<typeof deriveClientJourney>;
}

const ENQUIRY_SOURCES = ['Website', 'WhatsApp', 'Apple Mail', 'Telephone'];

type SortColumn = 'client' | 'where' | 'next' | 'activity';
type SortState = { column: SortColumn; direction: 'asc' | 'desc' };

function nextSort(current: SortState | null, column: SortColumn): SortState {
  if (!current || current.column !== column) {
    return { column, direction: column === 'activity' ? 'desc' : 'asc' };
  }
  return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

function textOrder(left: string, right: string) {
  return left.localeCompare(right, 'en', { sensitivity: 'base', numeric: true });
}

function activityOrder(left: string, right: string, direction: 'asc' | 'desc') {
  const leftTime = Date.parse(left || '');
  const rightTime = Date.parse(right || '');
  const leftMissing = Number.isNaN(leftTime);
  const rightMissing = Number.isNaN(rightTime);
  if (leftMissing || rightMissing) {
    if (leftMissing && rightMissing) return 0;
    return leftMissing ? 1 : -1;
  }
  const diff = leftTime - rightTime;
  return direction === 'asc' ? diff : -diff;
}

function SortHeader({
  column,
  label,
  sort,
  onSort,
}: {
  column: SortColumn;
  label: string;
  sort: SortState | null;
  onSort: (column: SortColumn) => void;
}) {
  const active = sort?.column === column;
  const ascending = !sort || sort.direction === 'asc';
  return (
    <th aria-sort={!active ? 'none' : ascending ? 'ascending' : 'descending'}>
      <button
        type="button"
        className={active ? 'studio-sort is-sorted' : 'studio-sort'}
        onClick={() => onSort(column)}
        data-testid={`sort-${column}`}
      >
        {label}
        <span className="studio-sort-mark" aria-hidden="true">{active ? (ascending ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );
}

function updatedLabel(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

function jobDetail(parts: Array<string | undefined>) {
  return parts.map((part) => String(part || '').trim()).filter(Boolean).join(' · ');
}

/** Filter chips on the list; "lost" is folded away unless chosen, closed jobs are shown as a stage. */
const STAGE_FILTERS: Array<{ id: JourneyStage | 'all'; label: string }> = [
  { id: 'all', label: 'Every stage' },
  ...STAGE_ORDER.map((stage) => ({ id: stage, label: STAGE_TITLES[stage] })),
  { id: 'lost', label: STAGE_TITLES.lost },
];

function StagePill({ stage, overdue }: { stage?: string; overdue?: boolean }) {
  if (!stage) return null;
  const title = STAGE_TITLES[stage as JourneyStage] || stage;
  return (
    <span className={`studio-stage-pill is-${stage}${overdue ? ' is-overdue' : ''}`} data-testid="stage-pill">
      {title}
    </span>
  );
}

function quoteContact(quote: StoredQuote) {
  const client = quote.client || {};
  const draft = quote.draft;
  return {
    name: String(client.name || draft?.clientName || '').trim(),
    email: String(client.email || draft?.recipientEmail || '').trim(),
    phone: String(client.phoneE164 || draft?.clientPhone || '').trim(),
    company: String(client.company || draft?.companyName || '').trim(),
    billingAddress: String(client.billingAddress || draft?.billingAddress || '').trim(),
  };
}

const StudioClients = ({
  onOpenBuilder,
  onOpenCalendar,
  onOpenShoot,
  startWithEnquiry = false,
  onConsumedStart,
  initialStage = '',
  onOpenClientRecord,
}: {
  onOpenBuilder?: (quote?: StoredQuote, returnClientKey?: string, editIssued?: boolean) => void;
  onOpenCalendar?: () => void;
  onOpenShoot?: (opts: { quoteId?: string; enquiryId?: string; tab?: ShootTab }) => void;
  startWithEnquiry?: boolean;
  onConsumedStart?: () => void;
  /** Pre-select a lifecycle stage filter, e.g. from the Today pipeline. */
  initialStage?: string;
  /** Opens the full client record: every job, the money, and the merged history. */
  onOpenClientRecord?: (key: string) => void;
}) => {
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [enquiries, setEnquiries] = useState<StudioEnquiry[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentSummaries, setPaymentSummaries] = useState<Record<string, PaymentSummary>>({});
  const [showEnquiry, setShowEnquiry] = useState(startWithEnquiry);
  const [allQuotes, setAllQuotes] = useState<StoredQuote[]>([]);
  const [bookingsByQuote, setBookingsByQuote] = useState<Record<string, {
    shootDate?: string;
    startTime?: string;
    dateConfirmed?: boolean;
    completedAt?: string;
    proposedShootDate?: string;
    deliveryDueOn?: string;
    deliveredAt?: string;
    closedAt?: string;
  }>>({});
  const [onlyMine, setOnlyMine] = useState(false);
  const [stageFilter, setStageFilter] = useState<string>(initialStage || 'all');

  useEffect(() => {
    setStageFilter(initialStage || 'all');
  }, [initialStage]);
  const [enquiryName, setEnquiryName] = useState('');
  const [enquiryEmail, setEnquiryEmail] = useState('');
  const [enquiryPhone, setEnquiryPhone] = useState('');
  const [enquirySource, setEnquirySource] = useState('Website');
  const [enquiryShoot, setEnquiryShoot] = useState('');
  const [enquiryDate, setEnquiryDate] = useState('');
  const [enquiryNotes, setEnquiryNotes] = useState('');
  const [enquiryMessage, setEnquiryMessage] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [removing, setRemoving] = useState(false);
  const [sort, setSort] = useState<SortState | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (startWithEnquiry) onConsumedStart?.();
  }, [startWithEnquiry, onConsumedStart]);

  const loadClients = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/clients/desk`);
      if (!response.ok) throw new Error('Clients could not be loaded.');
      const body = await response.json();
      setClients(Array.isArray(body.clients) ? body.clients : []);
      setEnquiries(Array.isArray(body.enquiries) ? body.enquiries : []);
      setAllQuotes(Array.isArray(body.quotes) ? body.quotes : []);
      const bookings = Array.isArray(body.bookings) ? body.bookings : [];
      setBookingsByQuote(Object.fromEntries(bookings.map((row: { quoteId: string }) => [row.quoteId, row])));
      const summaries = Array.isArray(body.payments) ? body.payments : [];
      setPaymentSummaries(Object.fromEntries(
        (summaries as Array<PaymentSummary & { quoteId: string }>).map((item) => [item.quoteId, item]),
      ));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Clients could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const quotePeople = useMemo(() => {
    const grouped = new Map<string, StoredQuote[]>();
    allQuotes.forEach((quote) => {
      if (!quote?.id || quote.deletedAt) return;
      const key = clientKeyForQuote(quote);
      grouped.set(key, [...(grouped.get(key) || []), quote]);
    });
    return Array.from(grouped.entries()).map(([key, quotes]) => {
      const newestAt = quotes
        .map((quote) => String(quote.updatedAt || quote.issuedAt || quote.createdAt || ''))
        .sort()
        .reverse()[0] || '';
      const current = pickCurrentQuote(quotes) || quotes[0];
      const contact = quoteContact(current);
      const names = Array.from(new Set(quotes.map((quote) => quoteContact(quote).name).filter(Boolean)));
      const server = clients.find((client) => client.key === key);
      const name = contact.name || server?.name || 'Unnamed client';
      return {
        kind: 'client' as const,
        key,
        name,
        alsoKnown: names.filter((item) => item !== name),
        contact: [contact.email || server?.email, contact.phone || server?.phoneE164].filter(Boolean).join(' · '),
        company: contact.company || server?.company || '',
        billingAddress: contact.billingAddress || server?.billingAddress || '',
        latestAt: newestAt || server?.latestAt || '',
      };
    });
  }, [allQuotes, clients]);

  const jobsByClient = useMemo(() => {
    const keys = new Set<string>([
      ...quotePeople.map((person) => person.key),
      ...clients.map((client) => client.key),
      ...enquiries.map((enquiry) => enquiry.clientKey),
    ]);
    const result = new Map<string, ResolvedJob[]>();
    keys.forEach((key) => {
      const jobs = listClientJobs(key, allQuotes, enquiries, (quote) => clientKeyForQuote(quote))
        .map((ref): ResolvedJob | null => {
          if (ref.kind === 'quote') {
            const quote = allQuotes.find((item) => item.id === ref.id);
            if (!quote) return null;
            return {
              kind: 'quote',
              id: quote.id,
              at: ref.at,
              title: quote.quoteNumber || 'Quotation',
              detail: jobDetail([
                clientFacingShootType(quote.shootType),
                formatPreferredDate(quote.preferredDate),
              ]),
              journey: deriveClientJourney(quote, paymentSummaries[quote.id], bookingsByQuote[quote.id]),
            };
          }
          const enquiry = enquiries.find((item) => item.id === ref.id);
          if (!enquiry) return null;
          return {
            kind: 'enquiry',
            id: enquiry.id,
            at: ref.at,
            title: 'Client details',
            detail: jobDetail([
              enquiry.source,
              clientFacingShootType(enquiry.shootType),
              formatPreferredDate(enquiry.preferredDate),
            ]),
            journey: deriveClientJourney(null, null, null, enquiry),
          };
        })
        .filter((job): job is ResolvedJob => Boolean(job));
      result.set(key, jobs);
    });
    return result;
  }, [quotePeople, clients, enquiries, allQuotes, paymentSummaries, bookingsByQuote]);

  const enquiryOnlyRows = useMemo(() => {
    const known = new Set([
      ...quotePeople.map((person) => person.key),
      ...clients.map((client) => client.key),
    ]);
    const grouped = new Map<string, StudioEnquiry[]>();
    enquiries.filter((enquiry) => !known.has(enquiry.clientKey)).forEach((enquiry) => {
      grouped.set(enquiry.clientKey, [...(grouped.get(enquiry.clientKey) || []), enquiry]);
    });
    return Array.from(grouped.values()).map((items) => {
      const latest = [...items].sort((left, right) => String(right.receivedAt || '').localeCompare(String(left.receivedAt || '')))[0];
      return {
        kind: 'enquiry' as const,
        key: latest.clientKey,
        name: latest.name,
        contact: [latest.email, latest.phone].filter(Boolean).join(' · '),
        company: '',
        latestAt: latest.receivedAt,
      };
    });
  }, [quotePeople, clients, enquiries]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = [
      ...quotePeople,
      ...enquiryOnlyRows,
    ];
    const matching = rows.flatMap((row) => {
      const jobs = jobsByClient.get(row.key) || [];
      const summary = summarizeClientJobs(jobs.map((job) => job.journey));
      if (onlyMine && !summary.needsAction) return [];
      if (stageFilter !== 'all' && !jobs.some((job) => job.journey.stage === stageFilter)) return [];
      if (needle) {
        const jobText = jobs.map((job) => [job.title, job.detail, job.journey.journeyStatus].join(' ')).join(' ');
        const names = 'alsoKnown' in row ? row.alsoKnown.join(' ') : '';
        const address = 'billingAddress' in row ? row.billingAddress : '';
        const haystack = [row.name, names, row.contact, row.company, address, jobText].join(' ').toLowerCase();
        if (!haystack.includes(needle)) return [];
      }
      const finished = jobs.filter((job) => job.journey.stage === 'closed' || job.journey.stage === 'delivered').length;
      return [{
        ...row,
        where: summary.label,
        next: summary.next,
        needsAction: summary.needsAction,
        stage: stageFilter !== 'all' ? stageFilter : summary.stage,
        overdue: jobs.some((job) => job.journey.deliveryOverdue),
        returning: jobs.length > 1 && finished > 0,
      }];
    });
    return matching
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        if (!sort) {
          const rank = (row: { needsAction: boolean }) => (row.needsAction ? 0 : 1);
          return rank(left.row) - rank(right.row) || left.index - right.index;
        }
        let diff = 0;
        if (sort.column === 'client') diff = textOrder(left.row.name, right.row.name);
        else if (sort.column === 'where') diff = textOrder(left.row.where, right.row.where);
        else if (sort.column === 'next') diff = textOrder(left.row.next, right.row.next);
        else if (sort.column === 'activity') return activityOrder(left.row.latestAt, right.row.latestAt, sort.direction) || left.index - right.index;
        else diff = 0;
        if (sort.direction === 'desc') diff = -diff;
        return diff || left.index - right.index;
      })
      .map(({ row }) => row);
  }, [quotePeople, enquiryOnlyRows, query, onlyMine, stageFilter, jobsByClient, sort]);

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    [...quotePeople.map((person) => person.key), ...enquiryOnlyRows.map((row) => row.key)].forEach((key) => {
      const stages = new Set((jobsByClient.get(key) || []).map((job) => job.journey.stage));
      stages.forEach((stage) => { counts[stage] = (counts[stage] || 0) + 1; });
    });
    return counts;
  }, [quotePeople, enquiryOnlyRows, jobsByClient]);

  const waitingOnMe = useMemo(
    () => [...quotePeople.map((person) => person.key), ...enquiryOnlyRows.map((row) => row.key)]
      .filter((key) => summarizeClientJobs((jobsByClient.get(key) || []).map((job) => job.journey)).needsAction)
      .length,
    [quotePeople, enquiryOnlyRows, jobsByClient],
  );

  const visibleKeys = visible.map((row) => row.key);
  const visibleKeyList = visibleKeys.join('\n');
  const selectedVisible = visibleKeys.filter((key) => selected.includes(key));
  const allVisibleSelected = visibleKeys.length > 0 && selectedVisible.length === visibleKeys.length;

  useEffect(() => {
    const allowed = new Set(visibleKeyList ? visibleKeyList.split('\n') : []);
    setSelected((current) => {
      const next = current.filter((key) => allowed.has(key));
      return next.length === current.length ? current : next;
    });
  }, [visibleKeyList]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedVisible.length > 0 && !allVisibleSelected;
    }
  }, [selectedVisible.length, allVisibleSelected]);

  const toggleKey = (key: string) => {
    setSelected((current) => (
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    ));
  };

  const toggleVisible = () => {
    setSelected((current) => {
      const every = visibleKeys.every((key) => current.includes(key));
      if (every) return current.filter((key) => !visibleKeys.includes(key));
      return Array.from(new Set([...current, ...visibleKeys]));
    });
  };

  const removeSelected = async () => {
    const keys = visible.filter((row) => selected.includes(row.key)).map((row) => row.key);
    if (!keys.length || removing) return;
    const who = keys.length === 1
      ? (visible.find((row) => row.key === keys[0])?.name || 'this client')
      : `${keys.length} clients`;
    if (!window.confirm(`Remove ${who}? Their quotations move to Recently Deleted and can be restored from Quotations. Enquiries are deleted. Payments and messages stay on record.`)) return;
    setRemoving(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/clients/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keys }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(body.error || 'Those clients could not be removed.');
        return;
      }
      setSelected([]);
      await loadClients();
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : 'Those clients could not be removed.');
    } finally {
      setRemoving(false);
    }
  };

  const openResolvedJob = (job: ResolvedJob) => {
    const tab = toShootTab(job.journey.nextTab || 'money');
    if (job.kind === 'quote') {
      onOpenShoot?.({ quoteId: job.id, tab });
      return;
    }
    onOpenShoot?.({ enquiryId: job.id, tab });
  };

  const openRow = (row: { key: string }) => {
    setError('');
    const jobs = jobsByClient.get(row.key) || [];
    if (jobs.length > 1) {
      if (onOpenClientRecord) {
        onOpenClientRecord(row.key);
        return;
      }
      openResolvedJob(jobs[0]);
      return;
    }
    if (jobs.length === 1) {
      openResolvedJob(jobs[0]);
      return;
    }
    if (onOpenClientRecord) {
      onOpenClientRecord(row.key);
      return;
    }
    setError('That client has no shoot or enquiry to open yet.');
  };

  const saveEnquiry = async () => {
    setEnquiryMessage('');
    setError('');
    const response = await fetch(`${API_BASE_URL}/studio/enquiries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: enquiryName,
        email: enquiryEmail,
        phone: enquiryPhone,
        source: enquirySource,
        shootType: enquiryShoot,
        preferredDate: enquiryDate,
        notes: enquiryNotes,
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body.error || 'The enquiry could not be saved.');
      return;
    }
    setEnquiryMessage('Enquiry saved.');
    setShowEnquiry(false);
    setEnquiryName('');
    setEnquiryEmail('');
    setEnquiryPhone('');
    setEnquiryShoot('');
    setEnquiryDate('');
    setEnquiryNotes('');
    await loadClients();
    if (body.id) {
      onOpenShoot?.({ enquiryId: body.id, tab: 'money' });
    }
  };

  return (
    <div className="studio-home" data-testid="clients-list">
      <header className="studio-home-head">
        <div>
          <h2>Clients</h2>
          <p>Open one job straight away, or open the client record when they have several. Select a client to remove them from this list.</p>
        </div>
        <div className="studio-home-actions">
          <button type="button" className="studio-primary" onClick={() => setShowEnquiry((open) => !open)} data-testid="new-enquiry">
            {showEnquiry ? 'Close enquiry' : 'New enquiry'}
          </button>
          {onOpenBuilder ? <button type="button" className="studio-secondary" onClick={() => onOpenBuilder()}>New quotation</button> : null}
          {onOpenCalendar ? <button type="button" className="studio-text-action" onClick={onOpenCalendar}>Calendar</button> : null}
        </div>
      </header>

      {showEnquiry ? (
        <section className="studio-panel" data-testid="enquiry-form">
          <h3 className="studio-section-label">New enquiry</h3>
          <p className="studio-note">Save without creating a quotation. Sources stay in the studio only.</p>
          <div className="studio-toolbar" style={{ flexWrap: 'wrap' }}>
            <label>Name<input value={enquiryName} onChange={(event) => setEnquiryName(event.target.value)} aria-label="Enquiry name" /></label>
            <label>Email<input value={enquiryEmail} onChange={(event) => setEnquiryEmail(event.target.value)} aria-label="Enquiry email" /></label>
            <label>Phone<input value={enquiryPhone} onChange={(event) => setEnquiryPhone(event.target.value)} aria-label="Enquiry phone" /></label>
            <label>
              Source
              <select value={enquirySource} onChange={(event) => setEnquirySource(event.target.value)} aria-label="Enquiry source">
                {ENQUIRY_SOURCES.map((source) => <option key={source} value={source}>{source}</option>)}
              </select>
            </label>
            <label>Shoot type<input value={enquiryShoot} onChange={(event) => setEnquiryShoot(event.target.value)} aria-label="Enquiry shoot type" /></label>
          </div>
          <PreferredDateField
            value={enquiryDate}
            onChange={setEnquiryDate}
            label="Preferred shoot date"
            dateLabel="Client has a date"
            laterLabel="Decide later"
          />
          <label className="studio-compose">
            Notes
            <textarea value={enquiryNotes} maxLength={2000} onChange={(event) => setEnquiryNotes(event.target.value)} aria-label="Enquiry notes" />
          </label>
          <div className="studio-toolbar">
            <button type="button" className="studio-primary" onClick={saveEnquiry} data-testid="save-enquiry">Save enquiry</button>
          </div>
        </section>
      ) : null}

      {enquiryMessage ? <p className="studio-note">{enquiryMessage}</p> : null}

      <div className="studio-toolbar">
        <input
          className="form-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search name, email, phone, or quotation"
          aria-label="Search clients"
          data-testid="clients-search"
        />
        <div className="studio-view-tabs" role="group" aria-label="Show clients">
          <button type="button" className={!onlyMine ? 'is-active' : ''} aria-pressed={!onlyMine} onClick={() => setOnlyMine(false)}>All</button>
          <button type="button" className={onlyMine ? 'is-active' : ''} aria-pressed={onlyMine} onClick={() => setOnlyMine(true)} data-testid="filter-needs-me">
            Needs me{waitingOnMe ? ` (${waitingOnMe})` : ''}
          </button>
        </div>
      </div>
      <div className="studio-stage-chips" role="group" aria-label="Filter by stage" data-testid="stage-filters">
        {STAGE_FILTERS.map((chip) => {
          const count = chip.id === 'all' ? undefined : (stageCounts[chip.id] || 0);
          if (chip.id === 'lost' && !count && stageFilter !== 'lost') return null;
          return (
            <button
              type="button"
              key={chip.id}
              className={`studio-stage-chip is-${chip.id}${stageFilter === chip.id ? ' is-active' : ''}`}
              aria-pressed={stageFilter === chip.id}
              onClick={() => setStageFilter(chip.id)}
              data-testid={`stage-filter-${chip.id}`}
            >
              {chip.label}{count !== undefined ? <em>{count}</em> : null}
            </button>
          );
        })}
      </div>
      {selectedVisible.length > 0 ? (
        <div className="studio-toolbar studio-selection-bar" data-testid="client-selection">
          <p className="studio-note">{selectedVisible.length === 1 ? '1 client selected' : `${selectedVisible.length} clients selected`}</p>
          <button type="button" className="studio-secondary" onClick={removeSelected} disabled={removing} data-testid="remove-clients">
            {removing ? 'Removing…' : 'Remove'}
          </button>
        </div>
      ) : null}
      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {loading ? <p className="studio-empty">Loading clients…</p> : null}
      {!loading && visible.length === 0 ? (
        <p className="studio-empty">{quotePeople.length === 0 && enquiries.length === 0 ? 'No clients yet.' : 'No clients match your search.'}</p>
      ) : null}
      {!loading && visible.length > 0 ? (
        <div className="studio-table-wrap">
          <table className="studio-table studio-table-sortable">
            <thead>
              <tr>
                <th className="studio-select-cell">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleVisible}
                    aria-label="Select all clients"
                    data-testid="select-all-clients"
                  />
                </th>
                <SortHeader column="client" label="Client" sort={sort} onSort={(column) => setSort((current) => nextSort(current, column))} />
                <SortHeader column="where" label="Where they are" sort={sort} onSort={(column) => setSort((current) => nextSort(current, column))} />
                <SortHeader column="next" label="Next step" sort={sort} onSort={(column) => setSort((current) => nextSort(current, column))} />
                <SortHeader column="activity" label="Last activity" sort={sort} onSort={(column) => setSort((current) => nextSort(current, column))} />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const isSelected = selected.includes(row.key);
                return (
                  <tr
                    key={`${row.kind}-${row.key}`}
                    className={isSelected ? 'is-selected' : undefined}
                    onClick={() => openRow(row)}
                    style={{ cursor: 'pointer' }}
                    data-testid={`client-row-${row.key}`}
                  >
                    <td
                      className="studio-select-cell"
                      data-label="Select"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleKey(row.key)}
                        aria-label={`Select ${row.name}`}
                        data-testid={`select-client-${row.key}`}
                      />
                    </td>
                    <td data-label="Client">
                      <strong>{row.name}</strong>
                      {row.returning ? <span className="studio-returning" title="Has booked before">Returning</span> : null}
                      {'alsoKnown' in row && row.alsoKnown.length > 0 ? <div className="studio-row-sub">{row.alsoKnown.join(' · ')}</div> : null}
                      {row.company ? <div>{row.company}</div> : null}
                      <div className="studio-row-sub">
                        {row.contact || 'No contact details'}
                        {onOpenClientRecord && row.kind === 'client' ? (
                          <>
                            {' · '}
                            <button
                              type="button"
                              className="studio-text-action"
                              onClick={(event) => { event.stopPropagation(); onOpenClientRecord(row.key); }}
                              data-testid={`client-record-${row.key}`}
                            >
                              History
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                    <td data-label="Where they are">
                      <StagePill stage={row.stage} overdue={row.overdue} />
                      <div>{row.where}</div>
                    </td>
                    <td data-label="Next step">
                      <span className={row.needsAction ? 'studio-your-move' : 'studio-waiting'}>
                        {row.next}
                      </span>
                    </td>
                    <td data-label="Last activity">{updatedLabel(row.latestAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default StudioClients;
